import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { useFeedback } from "../../../../providers/feedback/feeedback";
import { assignVouchersToUser } from "../../../../services/merchant/merchant_actions";
import { getMerchantsByPincode } from "../../../../services/merchant/merchant_service";
import { getUsersForMerchant } from "../../../../services/app_users/app_users";
import { AppUsersList } from "../../../users/model/app_users_list_model";
import { purple } from "../../../../theme/colors";
import { Merchant } from "../../model/merchant_model";

interface AssignVouchersToUserDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type AppUserWithPincode = AppUsersList & { pincode?: string | number };

interface AssignVouchersToUserFormInputs {
  user_id: number;
  user: AppUsersList | null;
  merchant: Merchant | null;
}

export default function AssignVouchersToUserDialog({ open, onClose, onSuccess }: AssignVouchersToUserDialogProps) {
  const { handleSubmit, control, reset, formState: { errors }, setValue } = useForm<AssignVouchersToUserFormInputs>({
    defaultValues: { user_id: 0, user: null, merchant: null },
  });
  const { showSnackbar } = useFeedback();
  const [searchValue, setSearchValue] = useState<string>("");
  const [selectedUser, setSelectedUser] = useState<AppUsersList | null>(null);
  const [selectedMerchant, setSelectedMerchant] = useState<Merchant | null>(null);

  const userPincode = (selectedUser as AppUserWithPincode | null)?.pincode != null
    ? String((selectedUser as AppUserWithPincode).pincode)
    : null;

  // Fetch app users with search functionality
  const { data: appUsers, isLoading: isLoadingUsers } = useQuery(
    ["app-users-search", searchValue],
    async () => {
      const response = await getUsersForMerchant({
        size: 1000,
        page: 1,
        keyword: searchValue,
        ageFrom: null,
        ageTo: null,
        educationId: null,
        gender_id: null,
        incomeId: null,
        occupationId: null,
        stateId: null,
        isSubscribed: false,
        sortOrder: "DESC",
        startDate: "",
        endDate: "",
        city: null,
        district: null
      });
      return response.data.data.data.list;
    },
    {
      retry:false,
      enabled: open,
    }
  );

  // Fetch merchants by user's pincode when user is selected and has pincode
  const { data: merchantsByPincode, isLoading: isLoadingMerchants } = useQuery(
    ["merchants-by-pincode", userPincode],
    async () => {
      const response = await getMerchantsByPincode(userPincode!);
      console.log("response", response);
      
      return response.data.data?.data;
    },
    { enabled: open && !!userPincode && userPincode.length >= 5 }
  );

  const merchantsList: Merchant[] = Array.isArray(merchantsByPincode) ? merchantsByPincode : [];

  const handleClose = () => {
    if (!assignVouchersMutation.isLoading) {
      reset();
      setSearchValue("");
      setSelectedUser(null);
      setSelectedMerchant(null);
      onClose();
    }
  };

  const assignVouchersMutation = useMutation(assignVouchersToUser, {
    onSuccess: (data) => {
      showSnackbar(`Vouchers assigned to user successfully!`);
      onSuccess();
      onClose();
      reset();
      setSearchValue("");
      setSelectedUser(null);
      setSelectedMerchant(null);
    },
    onError: (error: any) => {
      showSnackbar(error?.response?.data?.error?.message || 'Failed to assign vouchers to user');
    },
  });

  const onSubmit = (data: AssignVouchersToUserFormInputs) => {
    if (!selectedUser) {
      showSnackbar('Please select a user');
      return;
    }
    if (userPincode && !selectedMerchant) {
      showSnackbar('Please select a merchant in the user\'s location');
      return;
    }
    const payload: Parameters<typeof assignVouchersToUser>[0] = {
      user_id: selectedUser.id,
      number_of_vouchers: 1,
    };
    if (selectedMerchant?.id) {
      payload.merchant_id = selectedMerchant.id;
    }
    assignVouchersMutation.mutate(payload);
  };

  const handleUserSelect = (user: AppUsersList | null) => {
    setSelectedUser(user);
    setSelectedMerchant(null);
    setValue('user_id', user?.id || 0);
    setValue('user', user);
    setValue('merchant', null);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" fontWeight="600">
          Assign Vouchers to User
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate sx={{ mt: 1 }}>
          <Controller
            name="user"
            control={control}
            rules={{ 
              required: 'User selection is required'
            }}
            render={({ field }) => (
              <Autocomplete
                {...field}
                options={appUsers || []}
                getOptionLabel={(option) => option ? `${option.name} (${option.mobileNumber})` : ''}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                loading={isLoadingUsers}
                loadingText="Searching users..."
                noOptionsText={searchValue.length < 2 ? "Type at least 2 characters to search" : "No users found"}
                onInputChange={(event, newInputValue) => {
                  setSearchValue(newInputValue);
                }}
                onChange={(event, newValue) => {
                  handleUserSelect(newValue);
                  field.onChange(newValue);
                }}
                value={selectedUser}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    margin="normal"
                    required
                    fullWidth
                    label="Search User"
                    placeholder="Type name to search"
                    error={!!errors.user}
                    // helperText={errors.user?.message || "Start typing to search for users"}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isLoadingUsers ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body1" fontWeight="500">
                        {option.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {option.mobileNumber} • {option.state}
                      </Typography>
                    </Box>
                  </Box>
                )}
              />
            )}
          />

          {selectedUser && (
            <>
              <TextField
                margin="normal"
                fullWidth
                label="Pincode"
                value={userPincode ?? "—"}
                InputProps={{ readOnly: true }}
                size="small"
              />
              {userPincode && (
                <Controller
                  name="merchant"
                  control={control}
                  rules={{ required: userPincode ? 'Please select a merchant in this location' : false }}
                  render={({ field }) => (
                    <Autocomplete<Merchant>
                      {...field}
                      options={merchantsList}
                      getOptionLabel={(option) => option ? `${option.name}` : ''}
                      isOptionEqualToValue={(option, value) => option?.id === value?.id}
                      loading={isLoadingMerchants}
                      loadingText="Loading merchants..."
                      noOptionsText={isLoadingMerchants ? "Loading..." : "No merchants found in this pincode"}
                      onChange={(_, newValue) => {
                        setSelectedMerchant(newValue);
                        field.onChange(newValue);
                      }}
                      value={selectedMerchant}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          margin="normal"
                          required={!!userPincode}
                          fullWidth
                          label="Merchant (in this location)"
                          placeholder="Select merchant"
                          error={!!errors.merchant}
                          helperText={errors.merchant?.message}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {isLoadingMerchants ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      renderOption={(props, option) => (
                        <Box component="li" {...props}>
                          <Box>
                            <Typography variant="body1" fontWeight="500">
                              {option.name}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    />
                  )}
                />
              )}
            </>
          )}
          
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button onClick={handleClose} disabled={assignVouchersMutation.isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          disabled={assignVouchersMutation.isLoading}
          sx={{ backgroundColor: purple }}
        >
          {assignVouchersMutation.isLoading ? 'Assigning...' : 'Assign Vouchers'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
